<template>
  <header class="main-nav" :class="{ 'menu-open': showMenu > 0 }">
    <div class="header-bar">
      <a class='logotype' href='/'>
        <Logo />
        <LogoType />
      </a>
      <Responsive v-slot="{ breakpoint }">
        <div class="nav-container">
          <NavSections v-if='breakpoint != "phone"' :sections="navigableSections"/>
          <LanguageSwitcher v-if='breakpoint != "phone"'/>
          <MenuToggle v-if='breakpoint == "phone"' :open="showMenu > 0" @click="showMenu = showMenu > 0 ? 0 : 1"/>
        </div>
      </Responsive>
    </div>
  </header>

  <Teleport to="body">
    <Responsive v-slot="{ breakpoint }">
      <div v-if='breakpoint == "phone" && showMenu > 0' class="mobile-menu-overlay">
        <div class="mobile-menu-header">
          <a class='logotype' href='/' @click="showMenu = 0">
            <Logo />
            <LogoType />
          </a>
          <MenuToggle :open="true" @click="showMenu = 0"/>
        </div>
        <MenuStacked :sections="navigableSections" />
        <div class="mobile-menu-footer">
          <LanguageSwitcher :mobile="true" />
        </div>
      </div>
    </Responsive>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue';
import NavSections from './NavSections.vue';
import LanguageSwitcher from './LanguageSwitcher.vue';
import MenuToggle from './MenuToggle.vue';
import MenuStacked from './MenuStacked.vue';
import { composables } from '@/composables';

const showMenu = composables().showMenu;

const props = defineProps({
  kql: {
    type: Object,
    required: true
  }
});

// Compute navigable sections from page blocks
const navigableSections = computed(() => {
  if (!props.kql.page?.layout || !Array.isArray(props.kql.page.layout)) return [];

  return props.kql.page.layout
    .map(block => {
      // Only use nav_title if it exists and is not empty
      // Don't fall back to section_title - that allows hiding sections from nav
      const navTitle = block.content?.nav_title?.trim();
      return navTitle ? {
        id: block.id,
        nav_title: navTitle,
        anchorId: navTitle.toLowerCase().replace(/\s+/g, '-')
      } : null;
    })
    .filter(section => section !== null);
});
</script>

<style scoped>
@import '@/css/breakpoints.css';

  header {
    box-sizing: border-box;
    z-index: 99;
    margin-inline: var(--main-margin);
    margin-top: 4rem;
    font-size: var(--text-lg);
    font-weight: var(--font-semibold);
    background-color: var(--orange);
    color: var(--mahogany);
    width: var(--main-width);
    border-radius: 4.25rem;
    transition: min-height 0.3s ease-in-out;
    position: relative;
    container-type: inline-size;
  }

  @media (--phone) {
    header {
      margin-top: 1rem;
    }
  }

  .nav-container {
    padding-left: 1em;
  }

  header.menu-open {
    opacity: 0;
    pointer-events: none;
  }

  .header-bar {
    padding: 2rem 1.5rem;
    height: 8.5rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    overflow: hidden;
  }

  a.logotype {
    display: flex;
    align-items: center;
    gap: clamp(12px, 1vw, 2rem);
  }

  .nav-container {
    display: flex;
    align-items: center;
    gap: 2rem;
  }

  .mobile-menu-overlay {
    position: fixed;
    top: 1rem;
    left: 50%;
    transform: translateX(-50%);
    bottom: 4rem;
    width: var(--main-width);
    height: auto;
    background-color: var(--orange);
    color: var(--mahogany);
    z-index: 9999;
    overflow: auto;
    display: flex;
    flex-direction: column;
    border-radius: 4.25rem;
    box-sizing: border-box;
  }

  .mobile-menu-header {
    padding: 2rem 1.5rem;
    height: 8.5rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
  }

  .mobile-menu-footer {
    padding: 2rem 1.5rem;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-shrink: 0;
  }
</style>
